import { Container, ContainerChild, Sprite, Texture } from "pixi.js";

import {
  renderPlaylistDesignSvg,
  type PlaylistDesign,
  type PlaylistDesignRow,
} from "../../../../data/playlistDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/**
 * Design-store render strategy for the Playlist widget — sibling of the
 * goal/viewer design renderers. Renders the chosen design's CARD SVG (from the
 * local catalog) as a single Pixi sprite stretched to the widget bounds,
 * re-rasterising the texture only when the displayed rows actually change.
 *
 * It does NOT own the data source: the parent `PlaylistWidgetRenderer` already
 * subscribes to the playlist `liveQuery`, so it just calls {@link update} with
 * the resolved rows (title + optional duration). One instance per widget (kept
 * in the parent's Map).
 */
export class PlaylistDesignRenderer {
  private container: Container | null = null;

  private sprite: Sprite | null = null;

  private destWidth = 1;

  private destHeight = 1;

  private renderScale = 1;

  /** Last rows key baked into the current texture. */
  private lastKey: string | null = null;

  /** Most recent rows pushed in (re-drawn once fonts finish decoding). */
  private lastRows: PlaylistDesignRow[] | null = null;

  /** Texture currently shown — destroyed before being replaced. */
  private texture: Texture | null = null;

  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  private generation = 0;

  private destroyed = false;

  constructor(private readonly design: PlaylistDesign) {}

  render(
    parentContainer: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    this.container = new Container();
    parentContainer.addChild(this.container);

    this.destWidth = Math.max(1, destination.width);
    this.destHeight = Math.max(1, destination.height);

    const scaleForBounds = Math.max(
      1,
      this.destWidth / this.design.cardWidth,
      this.destHeight / this.design.cardHeight,
    );
    this.renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = this.destWidth;
    sprite.height = this.destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);
    this.sprite = sprite;

    // Preload the design's Google fonts, then force a redraw with the latest
    // rows so the real typeface (not a system fallback) is baked in.
    void ensureDesignFontsReady(this.design.fonts).then(() => {
      if (this.destroyed) return;
      this.lastKey = null;
      if (this.lastRows) void this.rerasterize(this.lastRows);
    });
  }

  /** Push fresh rows; re-rasterises only when the row list changes. */
  update(rows: PlaylistDesignRow[]): void {
    if (this.destroyed || !this.sprite) return;
    this.lastRows = rows;
    const key = this.keyOf(rows);
    if (key === this.lastKey) return;
    void this.rerasterize(rows);
  }

  private keyOf(rows: PlaylistDesignRow[]): string {
    return rows
      .slice(0, this.design.rowSlots)
      .map((r) => `${r.title}${r.duration}`)
      .join("");
  }

  private async rerasterize(rows: PlaylistDesignRow[]): Promise<void> {
    if (this.destroyed || !this.sprite) return;

    // Claim this generation/key immediately so the equality guard in update()
    // doesn't fire a second raster for the same rows mid-decode.
    this.lastKey = this.keyOf(rows);
    const gen = ++this.generation;

    const svg = renderPlaylistDesignSvg(this.design, { rows });

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        this.renderScale,
      );

      if (
        this.destroyed ||
        gen !== this.generation ||
        !this.sprite ||
        this.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = this.texture;
      this.texture = texture;
      this.sprite.texture = texture;
      // Assigning a texture in Pixi v8 resets the sprite to the texture's
      // intrinsic size — re-assert the widget bounds.
      this.sprite.width = this.destWidth;
      this.sprite.height = this.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      if (!this.destroyed && gen === this.generation) {
        this.lastKey = null;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;
    this.sprite = null;
    this.lastRows = null;

    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
